/**
 * Controlled logger utility for development and production environments.
 * In production, logs are suppressed unless explicitly enabled.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Check if we're in development mode
const isDev = import.meta.env.DEV;

// Global configuration
const config: LoggerConfig = {
  enabled: isDev, // Only enabled in development by default
  level: isDev ? 'debug' : 'error',
  prefix: '[Copilot]',
};

/**
 * Configure the logger settings.
 */
export function configureLogger(options: Partial<LoggerConfig>): void {
  Object.assign(config, options);
}

/**
 * Check if a log level should be output.
 */
function shouldLog(level: LogLevel): boolean {
  if (!config.enabled) return false;
  return LOG_LEVELS[level] >= LOG_LEVELS[config.level];
}

/**
 * Format log arguments with prefix and timestamp.
 */
function formatArgs(level: LogLevel, args: unknown[]): unknown[] {
  const timestamp = new Date().toISOString().slice(11, 23);
  return [`${config.prefix} [${timestamp}] [${level.toUpperCase()}]`, ...args];
}

/**
 * Logger object with methods for each log level.
 */
export const logger = {
  /**
   * Log debug information (development only).
   */
  debug: (...args: unknown[]): void => {
    if (shouldLog('debug')) {
      console.debug(...formatArgs('debug', args));
    }
  },

  /**
   * Log general information.
   */
  info: (...args: unknown[]): void => {
    if (shouldLog('info')) {
      console.info(...formatArgs('info', args));
    }
  },

  /**
   * Log warnings.
   */
  warn: (...args: unknown[]): void => {
    if (shouldLog('warn')) {
      console.warn(...formatArgs('warn', args));
    }
  },

  /**
   * Log errors (always logged unless logger is disabled).
   */
  error: (...args: unknown[]): void => {
    if (shouldLog('error')) {
      console.error(...formatArgs('error', args));
    }
  },

  /**
   * Log a group of related messages.
   */
  group: (label: string, fn: () => void): void => {
    if (!config.enabled) return;

    console.group(`${config.prefix} ${label}`);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  },

  /**
   * Log performance timing.
   */
  time: (label: string): void => {
    if (shouldLog('debug')) {
      console.time(`${config.prefix} ${label}`);
    }
  },

  /**
   * End performance timing.
   */
  timeEnd: (label: string): void => {
    if (shouldLog('debug')) {
      console.timeEnd(`${config.prefix} ${label}`);
    }
  },

  /**
   * Log a table of data.
   */
  table: (data: unknown): void => {
    if (shouldLog('debug')) {
      console.table(data);
    }
  },
};

// Export default for convenience
export default logger;
